// client/src/utils/pricing.ts
// MIRRORED FILE — twin: src/utils/pricing.js. Change one, change both.
// `mirroredUtilParity.test.js` runs the two against the same inputs and fails on drift.
//
// Single source of truth for turning a quotation line's COST into its SELLING price,
// and for answering "what rate is this line quoted at".
//
// This file exists because the grid had its own copy. `QuotationDetailPage` carried
// `resolveMarkupPct` + `resolveLineSelling`, described in their own comments as
// "mirrors resolveItemPricing in src/utils/pricing.js" — and they did not. The server
// back-derived a line's rate from its stored selling price and rounded to 2dp; the grid
// back-derived it from a differently-rounded cost and rounded to 1dp; the exports and
// the screen printed different markups for the same line. Two copies of a money rule,
// drifting, is this codebase's oldest defect shape.
//
// Resolution order for a line's markup:
//   1. an explicit per-line `markup` (0 is a real value and is honoured),
//   2. else the rate that REPRODUCES the stored selling price, if one does,
//   3. else the ratio of the stored selling price to cost — the line was priced by hand,
//   4. else the house default below — the same number the server applies.

// The money rounding, from its owner. CLAUDE.md is explicit that a second local
// spelling of a rounder is this codebase's oldest defect shape, and totals are
// exactly where two spellings would show up as a cent of disagreement between
// the screen and the export.
import { ceilPrice, positiveNumber } from './priceUtils';
import { num, round2 } from './invoiceLineMath';
import { unitChanged } from './unitVocabulary';

export const DEFAULT_MARKUP_PERCENT = 30;

// A rate an operator can actually APPLY, as opposed to one merely MEASURED
// off a hand-typed price (resolveMarkupPercent's 'derived' source is left
// unclamped on purpose). Below -100% a "selling" price is negative; -99%
// still lets a line be quoted at 1% of cost. 500% is generous headroom above
// any real house rate. Sourced from the clamp `runAutoAdjustMarkup` already
// applied (QuotationDetailPage.tsx) — moved here so every write site is
// protected by construction instead of each caller remembering to clamp.
export const MIN_APPLIED_MARKUP_PERCENT = -99;
export const MAX_APPLIED_MARKUP_PERCENT = 500;

/** Bound a rate that is about to be APPLIED (never one merely measured). */
export const clampMarkupPercent = (rate: number | null | undefined): number => {
  const n = Number(rate);
  if (!Number.isFinite(n)) return n;
  return Math.max(MIN_APPLIED_MARKUP_PERCENT, Math.min(MAX_APPLIED_MARKUP_PERCENT, n));
};

/** The shape this module reads. Every field is optional — a line may carry none of them. */
export interface PricedLine {
  unitPrice?: number | null;
  totalPrice?: number | null;
  /** The TT$ this line's cost was STATED as, pinned at write time — see `unitCostTtd`. */
  unitPriceTtd?: number | null;
  /** The unrounded US$ cost `unitPrice` was ceiled from (D85) — see `markupCostUsd`. */
  unitPriceExact?: number | null;
  /**
   * Read by the TT$ cost owners below, and by `resolveItemPricing` to extend the rounded
   * unit selling price (USC-BUG-0038). `priceLineAt` still takes it as an argument.
   */
  quantity?: number | null;
  markup?: number | null;
  unitSellingPrice?: number | null;
  totalSellingPrice?: number | null;
  /** D97: the selling price this line must sell at (an IFS list price); see `priceLineAt`. */
  statedSellingPrice?: number | null;
}

export type MarkupSource = 'line' | 'reproduced' | 'derived' | 'default';

/**
 * The selling price of one unit at a rate — the ONE expression for it.
 *
 * Every caller that turns a cost and a percentage into money goes through here,
 * so "the price the rate produces" has a single definition. That matters more
 * than it looks: `resolveMarkupPercent` decides what rate a line is quoted at by
 * asking which rate reproduces the stored price, and a second, slightly
 * different spelling of this expression anywhere would make that question
 * unanswerable for the lines that spelling wrote.
 *
 * `ceilPrice` here is the grid's own (utils/priceUtils), which returns its input
 * unchanged for null/NaN where the server's returns 0. The guard above it means
 * neither side ever reaches that difference through this function, and the
 * parity harness pins it with those inputs.
 */
export const sellingPriceAt = (
  cost: number | null | undefined,
  markupPercent: number | null | undefined
): number => {
  const c = Number(cost);
  const rate = Number(markupPercent);
  if (!Number.isFinite(c) || c <= 0 || !Number.isFinite(rate)) return 0;
  return ceilPrice(c * (1 + clampMarkupPercent(rate) / 100));
};

/**
 * The US$ cost a markup is applied to — D85. Twin of `markupCostUsd` in
 * src/utils/pricing.js (see the full rationale there): the line's stored UNROUNDED
 * cost (`unitPriceExact`) when it still explains the stored cost
 * (`ceilPrice(exact) === ceilPrice(unitCostUsd)`), so the price is rounded up once
 * instead of twice; else the stored US$ cost. UNROUNDED.
 */
export const markupCostUsd = (
  unitCostUsd: unknown,
  unitCostExact: unknown
): number => {
  const exact = positiveNumber(unitCostExact);
  if (exact != null && ceilPrice(exact) === ceilPrice(Number(unitCostUsd))) return exact;
  return Number(unitCostUsd) || 0;
};

/**
 * The rate a line is quoted at, and where that answer came from.
 *
 * A markup rate is an INPUT. It is not recoverable by measurement from a price
 * that has been quantised, and quotation prices are quantised hard: every
 * selling price is `ceilPrice`d to the next cent, deliberately, so a line is
 * never quoted under its target. Dividing the rate back out of that price
 * returns the rate PLUS up to one cent's worth of the cost — which on a $3.44
 * line is 0.29 percentage points, and on a $0.09 line is 3.3. That is where
 * "I set 30% and it says 30.2%" came from: 825 of 1,539 QA quotation lines
 * displayed a rate nobody had set, while only 3 lines stored a fractional one.
 *
 * So the question is not "what ratio do these two numbers make" but "which rate
 * PRODUCED this price". Recompute the candidate and compare, exactly. No
 * tolerance band, on purpose — "within a cent of 30%" is the same guesswork in
 * a smaller coat, and it would quietly relabel a hand-typed price as a
 * house-rate one.
 */
export const resolveMarkupPercent = (
  item: PricedLine | null | undefined,
  defaultMarkupPercent: number = DEFAULT_MARKUP_PERCENT
): { markupPercent: number; source: MarkupSource } => {
  const parsedDefault = Number(defaultMarkupPercent);
  const houseRate = Number.isFinite(parsedDefault) ? parsedDefault : 0;

  if (item?.markup != null && Number.isFinite(Number(item.markup))) {
    return { markupPercent: Number(item.markup), source: 'line' };
  }

  // Unit pair first, then total — the same precedence the price uses below, so
  // the rate and the money are never read off different halves of the line.
  const pairs: Array<[number, number]> = [
    [Number(item?.unitPrice) || 0, Number(item?.unitSellingPrice) || 0],
    [Number(item?.totalPrice) || 0, Number(item?.totalSellingPrice) || 0],
  ];
  for (const [cost, selling] of pairs) {
    if (!(cost > 0) || !(selling > 0)) continue;
    if (sellingPriceAt(cost, houseRate) === selling) {
      return { markupPercent: houseRate, source: 'reproduced' };
    }
    return { markupPercent: (selling / cost - 1) * 100, source: 'derived' };
  }

  return { markupPercent: houseRate, source: 'default' };
};

/**
 * The rate to APPLY when re-pricing a line — a different question from the one
 * above, and the difference is not cosmetic.
 *
 * `resolveMarkupPercent` reports what a line IS quoted at, and on a hand-priced
 * line that answer is measured off the stored price. A re-price is exactly the
 * moment that measurement stops being valid: the caller has already replaced the
 * cost and has not yet replaced the selling price, so the two numbers on the line
 * belong to different states of it. Feeding that ratio back in would price a
 * fresh $2.47 cost at whatever the last hand-typed price happened to imply.
 *
 * So a re-price applies the RULE the line carries — its own rate, else the house
 * rate — and never a rate inferred from a price it is in the middle of replacing.
 * A 0 is a real rate (quote this line at cost) and is honoured; `item.markup ||
 * house` is the shape that re-marked at-cost lines up by 30% on a quantity edit.
 */
export const markupToApply = (
  item: PricedLine | null | undefined,
  defaultMarkupPercent: number = DEFAULT_MARKUP_PERCENT
): number => {
  if (item?.markup != null && Number.isFinite(Number(item.markup))) return Number(item.markup);
  const parsedDefault = Number(defaultMarkupPercent);
  return Number.isFinite(parsedDefault) ? parsedDefault : 0;
};

/** The rate and the money a re-price produces, ready to spread onto a line. */
export interface PricedAtRate {
  /** Null only for a stated-price line with no cost to measure against (D97, invariant 8). */
  markup: number | null;
  unitSellingPrice: number;
  totalSellingPrice: number;
}

/**
 * Price a line at a rate, and answer with the rate AND the money together.
 *
 * This exists so a write site CANNOT record one without the other. "I set 30%
 * and it says 30.2%" was a reading bug, but its root was a writing one: the
 * price went to the database and the rate that produced it did not, so every
 * reader reconstructed the rate by dividing it back out of a number that had
 * been ceiled to the next cent — which returns the rate plus up to a cent's
 * worth of the cost.
 *
 * `resolveMarkupPercent` recovers those rows exactly, but only because of a
 * property of the CODE rather than of the data: a non-default rate can only
 * reach the arithmetic through `item.markup`, which is then stored, so a line
 * with a null `markup` was necessarily priced at the house rate. That property
 * was load-bearing and nothing enforced it — the day some path applied a rate
 * it did not record, the reproduce-check would fail over to the ratio and the
 * noise would be back. It was reachable from this very screen: converting a
 * line's unit re-ceiled both halves, and 3.44 -> 34.40 against 4.48 -> 44.80 no
 * longer reproduces at 30%. So the rate stopped being something a caller may
 * forget: every site that turns a cost and a percentage into a selling price
 * spreads this result onto the line, and `markup` travels with the money by
 * construction.
 *
 * Total Selling is Qty x Unit Selling — the invariant `Quotation.pre('save')`
 * enforces server-side, so deriving it any other way only hands the save hook
 * something to overwrite. (`resolveItemPricing` reads a MISSING total off the
 * total cost instead. Different question: that one is asked of a line nobody
 * has priced yet, where there is no unit selling price to multiply.)
 *
 * D85: pass `unitCostExact` (the line's stored unrounded US$ cost) and the markup
 * is applied to it, rounded up once — see `markupCostUsd`. Omit it and this is
 * exactly what it was.
 *
 * D97: pass `statedSellingPrice` (the line's stored one, if any) and the selling price is
 * PINNED - the cost may have moved, the price a customer was promised has not - so this answers
 * with `priceLineAtSelling` (rate re-measured, never clamped) and `markupPercent` is not used.
 * Every re-price site passes it, so the lock lives here and not in each caller.
 */
export const priceLineAt = ({
  unitCost,
  unitCostExact,
  quantity,
  markupPercent,
  statedSellingPrice,
}: {
  unitCost: number | null | undefined;
  unitCostExact?: number | null | undefined;
  quantity: number | null | undefined;
  markupPercent: number | null | undefined;
  statedSellingPrice?: number | null | undefined;
}): PricedAtRate => {
  const pinned = positiveNumber(statedSellingPrice);
  if (pinned != null) {
    return priceLineAtSelling({ unitCost, unitCostExact, quantity, sellingPrice: pinned });
  }
  const parsedRate = Number(markupPercent);
  // Clamped HERE, not just inside sellingPriceAt: `markup` is what gets
  // stored on the line, and it must be the rate that actually produced the
  // money, or `resolveMarkupPercent`'s 'line' source starts reporting a rate
  // that reproduces nothing.
  const markup = Number.isFinite(parsedRate) ? clampMarkupPercent(parsedRate) : 0;
  const unitSellingPrice = sellingPriceAt(markupCostUsd(unitCost, unitCostExact), markup);
  const qty = Number(quantity);
  // `unitSellingPrice > 0` is not redundant with the quantity guard: `ceilPrice`
  // answers -0 for a zero product, and -0 is a value that survives into a stored
  // document and out again to compare unequal to the 0 every reader writes.
  const totalSellingPrice =
    unitSellingPrice > 0 && Number.isFinite(qty) && qty > 0
      ? ceilPrice(unitSellingPrice * qty)
      : 0;
  return { markup, unitSellingPrice, totalSellingPrice };
};

/**
 * D97: a line whose SELLING price is a fact somebody else stated (an IFS list price) and whose
 * cost is ours. The inverse of `priceLineAt`: the selling price is the input, the rate is the
 * measurement. `unitSellingPrice` is `ceilPrice(sellingPrice)` (the one rounder every stored
 * price already goes through, so the pre-save hook cannot move it a cent), the total extends
 * that rounded unit price exactly as `priceLineAt` does, and `markup` is the realized rate on
 * the cost the markup would go on (`markupCostUsd`) - NEGATIVE when the stated price is under
 * cost, null when there is no cost to measure against. No selling price -> all zeros / null.
 * (Twin: src/utils/pricing.js.)
 */
export const priceLineAtSelling = ({
  unitCost,
  unitCostExact,
  quantity,
  sellingPrice,
}: {
  unitCost: number | null | undefined;
  unitCostExact?: number | null | undefined;
  quantity: number | null | undefined;
  sellingPrice: number | string | null | undefined;
}): { markup: number | null; unitSellingPrice: number; totalSellingPrice: number } => {
  const stated = positiveNumber(sellingPrice);
  const unitSellingPrice = stated != null ? ceilPrice(stated) : 0;
  const cost = markupCostUsd(unitCost, unitCostExact);
  const markup = realizedMarkupPercent(cost, unitSellingPrice);
  const qty = Number(quantity);
  const totalSellingPrice =
    unitSellingPrice > 0 && Number.isFinite(qty) && qty > 0 ? ceilPrice(unitSellingPrice * qty) : 0;
  return { markup, unitSellingPrice, totalSellingPrice };
};

/**
 * What the money actually earned over cost, as a percentage — the measurement,
 * as opposed to the rate.
 *
 * These are two different facts and conflating them is what this module's
 * second bug was. A 30% line whose price ceils up from $4.472 to $4.48 is quoted
 * at 30% and realizes 30.23%; both numbers are true and neither belongs in the
 * other's column. Anything reporting PROFIT wants this one. Anything reporting
 * the RULE the line is priced by wants `markup`.
 */
export const realizedMarkupPercent = (
  cost: number | null | undefined,
  selling: number | null | undefined
): number | null => {
  const c = Number(cost);
  const s = Number(selling);
  if (!Number.isFinite(c) || c <= 0 || !Number.isFinite(s) || s <= 0) return null;
  return (s / c - 1) * 100;
};

/**
 * D119.17.1: is a unit cost ABOVE a unit selling price? Only when the two, each stated at the cent
 * a price is held at (`ceilPrice`), differ that way, so a line sold exactly at cost never reads as
 * below cost because the exact cost carries float noise (670.0000000000001 against 670.00). False
 * when either figure is missing. The one owner of this question. (Twin: src/utils/pricing.js.)
 */
export const costAbovePrice = (
  unitCost: number | null | undefined,
  unitSellingPrice: number | null | undefined
): boolean => {
  const c = num(unitCost);
  const p = num(unitSellingPrice);
  return c !== null && p !== null && ceilPrice(c) > ceilPrice(p);
};

/** The code of the refusal when a locked line would change unit (server twin: AppError, same code). */
export const LOCKED_UNIT_CODE = 'QUOTATION_LINE_LOCKED_UNIT';

/**
 * D97: a locked line's unit selling price - the ONE answer. The line stores the stated (IFS list)
 * price WITH ITS UNIT (`statedSellingPrice` + `statedSellingUom`); only generation or the typed
 * unlock ever changes them, and a locked line NEVER changes unit. Same unit (`unitChanged`: blank
 * = blank, KGS = KG) -> exactly the stated price. Null = not locked. Locked in another unit ->
 * THROWS an Error carrying LOCKED_UNIT_CODE. Feed the result to
 * `priceLineAt({statedSellingPrice})`. (Twin: src/utils/pricing.js.)
 */
export const lockedUnitSellingPrice = (line: {
  statedSellingPrice?: number | string | null;
  statedSellingUom?: string | null;
  uom?: string | null;
} | null | undefined): number | null => {
  const stated = positiveNumber(line?.statedSellingPrice);
  if (stated == null || !line) return null;
  if (unitChanged(line.statedSellingUom, line.uom)) {
    throw Object.assign(
      new Error(`This line is locked at the IFS list price per ${line.statedSellingUom || '?'}. Type a selling price or markup to unlock it before changing its unit.`),
      { code: LOCKED_UNIT_CODE }
    );
  }
  return stated;
};

export interface ResolvedPricing {
  markup: number;
  markupSource: MarkupSource;
  realizedMarkup: number;
  unitCost: number;
  totalCost: number;
  unitSellingPrice: number;
  totalSellingPrice: number;
}

/**
 * D104.10: how a US$ COST is shown, everywhere a quotation line's cost is displayed (the grid, its
 * tooltips, the admin PDF): two decimals, or four when the cost has a sub-cent part, so US$0.2308
 * never reads as US$0.24 beside a profit worked out on 0.2308. Feed it the owner's cost
 * (`resolveItemPricing(...).unitCost/totalCost`), not a stored cent-ceiled `unitPrice`. (Twin.)
 */
export const exactCostText = (cost: number): string => {
  const n = Number(cost);
  if (!Number.isFinite(n)) return '0.00';
  const cents = n * 100;
  return Math.abs(cents - Math.round(cents)) < 1e-6 ? n.toFixed(2) : n.toFixed(4).replace(/0{1,2}$/, '');
};

/**
 * Resolve one quotation line's markup and selling prices.
 * A stored selling price always wins — it may have been hand-edited, and re-deriving it
 * from cost x markup would quietly discard that edit.
 */
export const resolveItemPricing = (
  item: PricedLine | null | undefined,
  defaultMarkupPercent: number = DEFAULT_MARKUP_PERCENT
): ResolvedPricing => {
  const storedUnitCost = Number(item?.unitPrice) || 0;
  // D104.10: COST is the exact cost (`markupCostUsd`, the figure the markup was taken from),
  // so cost, profit and realized markup agree with the selling price they measure. The stored
  // `unitPrice` is that cost ceiled to a cent; reading it overstated cost (QUO-00237: US$2,960.80
  // against an exact US$2,957.58). A line with no usable exact reads the stored cost as before.
  const unitCost = markupCostUsd(storedUnitCost, item?.unitPriceExact);
  const lineQty = Number(item?.quantity);
  const totalCost = unitCost !== storedUnitCost && Number.isFinite(lineQty) && lineQty > 0
    ? unitCost * lineQty
    : Number(item?.totalPrice) || 0;
  const storedUnitSelling = Number(item?.unitSellingPrice) || 0;
  const storedTotalSelling = Number(item?.totalSellingPrice) || 0;

  const { markupPercent: markup, source: markupSource } =
    resolveMarkupPercent(item, defaultMarkupPercent);

  const unitSellingPrice = storedUnitSelling > 0
    ? storedUnitSelling
    : sellingPriceAt(storedUnitCost, markup); // D104.10: pricing keeps the STORED cost (markup-cost-once); only cost/profit read the exact

  // Extended from the ROUNDED unit selling price, because that is the arithmetic the client
  // reads across the row: "13.16 x 10 = 131.60". This used to derive the total from the TOTAL
  // cost instead — ceiling the unrounded product once at the end — which multiplied the
  // per-unit ceiling residue AWAY and quoted 131.56 (USC-BUG-0038, prod, Quote Generator).
  // It also meant an untouched line and an edited one answered differently for the same
  // input, since `priceLineAt` and `Quotation.pre('save')` have always extended the rounded
  // unit price. All three now agree. A *stored* total still wins outright — it may have been
  // hand-typed. With no quantity there is nothing to extend, so the total cost keeps its old
  // reading: that is a line nobody has priced yet, where there is no rounded unit price to
  // multiply.
  const qty = Number(item?.quantity);
  const totalSellingPrice = storedTotalSelling > 0
    ? storedTotalSelling
    : unitSellingPrice > 0 && Number.isFinite(qty) && qty > 0
      ? ceilPrice(unitSellingPrice * qty)
      : sellingPriceAt(Number(item?.totalPrice) || 0, markup);

  // The unit pair first, so this measures the same two numbers the grid puts in
  // its Unit Cost and Unit Selling columns and a hand calculation reconciles.
  const realizedMarkup =
    realizedMarkupPercent(unitCost, unitSellingPrice) ??
    realizedMarkupPercent(totalCost, totalSellingPrice) ??
    markup;

  return {
    markup,
    markupSource,
    realizedMarkup,
    unitCost,
    totalCost,
    unitSellingPrice,
    totalSellingPrice,
  };
};

/**
 * A line whose selling price is STATED as 0 (free of charge, D96.6). resolveItemPricing reads a
 * stored 0 as "unset" and prices from cost + markup; a client invoice must bill 0.00 instead.
 * Mirrors isStatedFreeOfCharge in src/utils/pricing.js.
 */
export const isStatedFreeOfCharge = (item: PricedLine | null | undefined): boolean =>
  num(item?.unitSellingPrice) === 0;

/**
 * Is this line still priced at the rate it says it is?
 *
 * The Markup column states a RULE; the selling price is the money. They agree
 * on a line nobody has disturbed, and they come apart in exactly one way: the
 * cost moved after the line was priced and the selling price did not follow.
 * `resolveItemPricing` will not heal that — a stored selling price always wins
 * there, deliberately, because it may have been typed by hand.
 *
 * QUO-00013 (ClickUp 86d46vbf5): the catalogue went TT$103 -> TT$100, the line's
 * cost was refreshed to US$15.40, and US$20.60 stayed. The line reads "30%" and
 * earns 33.77% on a cost USC never paid. Nothing anywhere said so.
 *
 * This is deliberately only the QUESTION. It does not reprice: a quotation is a
 * record of what was quoted, and rewriting a past line's money because a
 * catalogue row moved is precisely what must not happen.
 *
 * True when there is nothing to contradict (no cost, or no selling price yet) —
 * an unpriced line is not a mispriced one.
 *
 * D85: a line is priced from its exact cost (`markupCostUsd`), so the stated rate
 * may reproduce the price from EITHER that or the stored US$ cost. Lines priced
 * before D85 used the stored US$ cost (marked up, ceiled twice) and are not stale;
 * both readings are exact, neither is a tolerance band.
 */
export const isPricedAtStatedRate = (
  item: PricedLine | null | undefined,
  defaultMarkupPercent: number = DEFAULT_MARKUP_PERCENT
): boolean => {
  const { markup, markupSource, unitCost, unitSellingPrice } =
    resolveItemPricing(item, defaultMarkupPercent);
  if (!(unitCost > 0) || !(unitSellingPrice > 0)) return true;
  // Only a line that STATES a rate can contradict one. On a hand-typed price the
  // rate is MEASURED off that very price ('derived'), so asking whether it
  // reproduces it is circular — and it would answer no for a reason that has
  // nothing to do with staleness: `sellingPriceAt` clamps at 500%, so a typed
  // price implying more than that reproduces nothing and would flag for ever.
  if (markupSource === 'derived') return true;
  // Exact, no tolerance band — the same discipline `resolveMarkupPercent` uses.
  // "Within a cent of the rate" would relabel a stale price as a rounding one,
  // which is the confusion this function exists to end.
  // D104.10: `unitCost` is already the exact cost; the pre-D85 reading marked up the STORED cost.
  return sellingPriceAt(unitCost, markup) === unitSellingPrice
    || sellingPriceAt(Number(item?.unitPrice) || 0, markup) === unitSellingPrice;
};

/** Sum of every line's selling total — the figure a quotation's grand total must match. */
export const sumSellingTotal = (
  items: Array<PricedLine> | null | undefined,
  defaultMarkupPercent: number = DEFAULT_MARKUP_PERCENT
): number =>
  (items || []).reduce(
    (sum, item) => sum + resolveItemPricing(item, defaultMarkupPercent).totalSellingPrice,
    0
  );

/** A discount is a percentage of a price. Outside 0..100 it is not one. */
export const clampDiscountPercent = (rate: number | null | undefined): number => {
  const n = Number(rate);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
};

export interface QuotationTotalsInput {
  defaultMarkupPercent?: number;
  discountPercent?: number;
  transportationCharges?: number;
  additionalCharges?: number;
  customExpensesTotal?: number;
}

export interface QuotationLineTotals {
  cost: number;
  sellingBeforeDiscount: number;
  discountAmount: number;
  netSellingPrice: number;
  chargeShare: number;
  profit: number;
  profitPercentOfCost: number;
  marginPercent: number;
}

export interface QuotationTotals {
  totalCost: number;
  sellingBeforeDiscount: number;
  discountPercent: number;
  discountAmount: number;
  netSellingPrice: number;
  charges: number;
  profit: number;
  profitPercentOfCost: number;
  marginPercent: number;
  finalPrice: number;
  lines: QuotationLineTotals[];
}

/**
 * Every figure in a quotation's Pricing Summary, from one place.
 *
 * Five sinks computed these independently — the summary box, the table's Item
 * Profit and Total Profit columns, the Excel export and the PDF export — and
 * four of them shared the same defect: `profit = selling - cost`, with the
 * discount nowhere in it. A quote discounted 10% reported the undiscounted
 * profit, so the number USC decided against was the profit of a quote it was
 * not sending. Margin % had it twice over: the discount was missing from the
 * numerator AND the denominator was the pre-discount selling price.
 *
 * The order the money actually moves, which is the order below:
 *
 *   selling before discount  S   = SUM(line selling)         "Total Price"
 *   discount                 D   = S x discount% / 100       "Discount"
 *   FINAL SELLING PRICE      Net = S - D
 *   profit                   Net - cost [- charges]          "Profit"
 *   margin %                 profit / Net x 100              "Margin %"
 *   final price              Net + charges                   "Final Price"
 *
 * MARKUP AND MARGIN ARE NOT THE SAME NUMBER and neither is an average of the
 * lines'. Markup is over COST and prices a line; margin is over the FINAL
 * SELLING PRICE and measures the quote. $100 at 30% markup less 10% discount is
 * $117, a $17 profit, and a 14.53% margin — not 30%, and not 20%.
 *
 * Charges (`transportationCharges`, `additionalCharges`, `customExpensesTotal`)
 * are ALWAYS billed on top of the goods: they raise Final Price and never touch
 * profit, because USC bills the client the same amount it pays out for them — a
 * wash, by construction.
 *
 * Charges built into the line prices are the server spread's (`src/utils/chargeSpread.js`, D170.5): it
 * raises the lines and leaves only the cents it cannot spread on a charge row, in one save, so this
 * bills whatever the charge fields hold, once, on top.
 */
export const resolveQuotationTotals = (
  items: ReadonlyArray<PricedLine> | null | undefined,
  options: QuotationTotalsInput = {}
): QuotationTotals => {
  const {
    defaultMarkupPercent = DEFAULT_MARKUP_PERCENT,
    discountPercent = 0,
    transportationCharges = 0,
    additionalCharges = 0,
    customExpensesTotal = 0,
  } = options || {};

  const priced = (items || []).map((item) => {
    const { totalCost, totalSellingPrice } = resolveItemPricing(item, defaultMarkupPercent);
    return { cost: totalCost, selling: totalSellingPrice };
  });

  const rawSelling = priced.reduce((sum, line) => sum + line.selling, 0);
  const rawCost = priced.reduce((sum, line) => sum + line.cost, 0);
  const rawCharges =
    (Number(transportationCharges) || 0) +
    (Number(additionalCharges) || 0) +
    (Number(customExpensesTotal) || 0);

  const pct = clampDiscountPercent(discountPercent);
  const rawDiscount = rawSelling * (pct / 100);
  const rawNet = rawSelling - rawDiscount;
  const rawProfit = rawNet - rawCost;

  // Per line, for the table's two profit columns. The discount is a flat rate on
  // selling, so a line's share of it IS its own selling x the rate — an identity,
  // not an apportionment. Rounding is applied once, at the end of each figure, so
  // a line can sit a cent off the summary row it feeds; the box is the authority
  // and the column is the explanation. `chargeShare` is always 0 now that charges
  // are never absorbed into profit here — kept on the shape rather than dropped,
  // since nothing outside this file reads it and a shape change is its own blast
  // radius for no behavioural gain.
  const lines = priced.map(({ cost, selling }) => {
    const lineNet = selling - selling * (pct / 100);
    const lineProfit = lineNet - cost;
    return {
      cost: round2(cost),
      sellingBeforeDiscount: round2(selling),
      discountAmount: round2(selling * (pct / 100)),
      netSellingPrice: round2(lineNet),
      chargeShare: 0,
      profit: round2(lineProfit),
      ...profitPercents(lineProfit, cost, lineNet),
    };
  });

  return {
    totalCost: round2(rawCost),
    sellingBeforeDiscount: round2(rawSelling),
    discountPercent: pct,
    discountAmount: round2(rawDiscount),
    netSellingPrice: round2(rawNet),
    charges: round2(rawCharges),
    profit: round2(rawProfit),
    // Profit over COST — the rate the quote realised, shown beside the money. Kept
    // distinct from marginPercent on purpose; they answer different questions and
    // printing either one under the other's label is the confusion this fixes.
    ...profitPercents(rawProfit, rawCost, rawNet),
    finalPrice: round2(rawNet + rawCharges),
    lines,
  };
};

export interface QuotationCharges {
  transportationCharges: number;
  additionalCharges: number;
  customExpensesTotal: number;
}

/**
 * The three charge figures a DOCUMENT states — twin of the server's
 * `chargesFromDoc` (pricing.js), missing-is-zero, parsed the way
 * `resolveQuotationTotals` parses them (`Number(x) || 0`). Pass the quotation,
 * or `{ transportationCharges, additionalCharges, customExpenses }` built from
 * screen state, or the competitor fields under the same names; the result
 * spreads straight into `resolveQuotationTotals`' options.
 *
 * Added 2026-09-26: `QuotationDetailPage` summed `customExpenses` inline in
 * five places. The charges TOTAL is still `resolveQuotationTotals(...).charges`.
 */
export const chargesFromDoc = (
  doc:
    | {
        transportationCharges?: unknown;
        additionalCharges?: unknown;
        customExpenses?: ReadonlyArray<{ amount?: unknown } | null | undefined> | null;
      }
    | null
    | undefined
): QuotationCharges => ({
  transportationCharges: Number(doc?.transportationCharges) || 0,
  additionalCharges: Number(doc?.additionalCharges) || 0,
  customExpensesTotal: (doc?.customExpenses || []).reduce((sum, e) => sum + (Number(e?.amount) || 0), 0),
});

/**
 * The pre-discount selling total that LANDS on `targetProfit` once the discount
 * is taken and the charges are paid — the inverse of the profit line above.
 *
 * Auto-adjust solved `S - C - P = target` and then let a discount take a slice
 * of S, so it missed its own target by exactly the discount amount, every time,
 * silently. The client pays `S(1-d)`, so the equation is
 * `S(1-d) - C - P = target`.
 *
 * Returns null at 100% off: no selling price reaches a positive profit when
 * every dollar of it is discounted away, and a caller must not be handed
 * Infinity to price a line with.
 */
export const requiredSellingForProfit = ({
  targetProfit = 0,
  totalCost = 0,
  charges = 0,
  discountPercent = 0,
}: {
  targetProfit?: number;
  totalCost?: number;
  charges?: number;
  discountPercent?: number;
} = {}): number | null => {
  const pct = clampDiscountPercent(discountPercent);
  if (pct >= 100) return null;
  const net = (Number(targetProfit) || 0) + (Number(totalCost) || 0) + (Number(charges) || 0);
  return round2(net / (1 - pct / 100));
};

/**
 * A US$ money figure restated as the TT$ shown beside it — the ONE expression
 * for that direction.
 *
 * `QuotationDetailPage.usdToTtd` (BUSINESS_RULES#usd-cost-and-ttd-display) and
 * `quotationExport.deriveTtdUnitCost` both delegate here rather than each
 * spelling `ceilPrice(usd / rate)` out again. Those two had already drifted
 * once: the export round-tripped through `.toFixed(2)`'s round-to-nearest
 * while the screen ceiled, so the same line printed a cent apart (ClickUp
 * 86d44afxj). Two spellings of a money rule is this codebase's oldest defect
 * shape, and the count only goes down.
 *
 * Null, never a fallback, when there is no usable rate: an absent rate is not
 * a rate of 1, and a US$ figure printed under a TT$ heading is
 * indistinguishable on screen from a real one (TECH_DEBT #156). The caller
 * shows a dash.
 */
export const ttdFromUsd = (
  usd: number | null | undefined,
  ttdToUsd: number | null | undefined
): number | null => {
  const exact = usdToTtdExact(usd, ttdToUsd);
  return exact == null ? null : ceilPrice(exact);
};

/**
 * D119.17: the UNROUNDED TT$ for a US$ figure - what a client-invoice line stores as its COST on a TT$ invoice
 * (a line keeps one exact cost; only its selling price is rounded up, once, from it). {@link ttdFromUsd} is this
 * ceiled, and keeps the selling-price and display uses. Same refusals: null for a missing / non-numeric figure
 * and for no usable rate. -0 reads 0. `ttdToUsd` is US$ per TT$1.
 */
export const usdToTtdExact = (
  usd: number | null | undefined,
  ttdToUsd: number | null | undefined
): number | null => {
  if (usd == null) return null;
  const n = Number(usd);
  if (!Number.isFinite(n)) return null;
  const rate = Number(ttdToUsd);
  if (!Number.isFinite(rate) || rate <= 0) return null;
  return n / rate + 0; // + 0: -0 + 0 is 0
};

/**
 * The TT$ a quotation line's COST is stated at.
 *
 * USC buys in Trinidad dollars — the Master Catalogue, the vendor invoices and
 * the price lists all state TT$ — but a quotation line stores US$, because
 * that is what the markup is applied to. Deriving the TT$ back out of that
 * stored US$ cannot return the figure it came from: TT$100.00 at 6.5 is
 * US$15.384615, which is not a whole cent, so it is stored ceiled to US$15.39
 * and reads back as TT$100.04. Every further restatement ceils again — a
 * CASE -> PCS -> CASE trip through the Unit dropdown lands US$15.40, TT$100.10
 * — and the drift only ever grows. QUO-00013 carried two lines priced off the
 * same TT$100.00 catalogue row reading TT$100.04 and TT$100.10.
 *
 * So the TT$ a line was PRICED FROM is pinned at write time, the same way
 * `fxRateAt*` pins a rate and for the same reason (invariant 5), and this
 * reads it back. `Quotation.items[].unitPriceTtd` is the pin; it IS the
 * stated figure and is returned untouched, never re-rounded.
 *
 * The derivation is the fallback, not the rule — for a line whose cost was
 * genuinely stated in US$ (a historical quotation, a price list), and for
 * every line written before the field existed. A line carrying no cost at all
 * reads 0, matching `resolveItemPricing`; null means only "there is no rate
 * to say it with".
 *
 * Owner decision 2026-09-01: the COST side only. Selling prices are still
 * computed from the US$ cost and restated with `ttdFromUsd`, so no figure
 * already quoted to a client moves. (D85: the pin is display only — the markup
 * goes on `unitPriceExact`, never on this.)
 */
export const unitCostTtd = (
  item: PricedLine | null | undefined,
  ttdToUsd: number | null | undefined
): number | null => {
  const pinned = item ? item.unitPriceTtd : null;
  if (pinned != null) {
    const n = Number(pinned);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  const usd = Number(item ? item.unitPrice : null);
  return ttdFromUsd(Number.isFinite(usd) ? usd : 0, ttdToUsd);
};

/**
 * Qty x unit cost, in TT$.
 *
 * Derived from the UNIT figure and never from the stored `totalPrice`, so the
 * TT$ pair obeys the same "Total == Qty x Unit" rule `Quotation.pre('save')`
 * enforces on the US$ pair (ClickUp 86d3t6v1d). Converting the stored total
 * instead would round a second, independent time and let the two columns
 * disagree about one line.
 */
export const totalCostTtd = (
  item: PricedLine | null | undefined,
  ttdToUsd: number | null | undefined
): number | null => {
  const unit = unitCostTtd(item, ttdToUsd);
  if (unit == null) return null;
  const qty = Number(item ? item.quantity : null);
  if (!Number.isFinite(qty)) return null;
  return ceilPrice(unit * qty);
};

/**
 * A quotation's total cost in TT$ — the sum of the LINES' TT$, not the TT$ of
 * their US$ sum. Restating the total instead would throw away every line's pin
 * at the last step and put the summary back a cent or two from the column it
 * totals.
 *
 * Null as soon as one line cannot state a TT$ figure, which can only be a
 * missing rate: a total that silently omits a line is worse than a dash.
 */
export const sumCostTtd = (
  items: ReadonlyArray<PricedLine> | null | undefined,
  ttdToUsd: number | null | undefined
): number | null => {
  const list = Array.isArray(items) ? items : [];
  let total = 0;
  for (const item of list) {
    const line = totalCostTtd(item, ttdToUsd);
    if (line == null) return null;
    total += line;
  }
  return ceilPrice(total);
};

/**
 * A quotation's PROFIT in TT$ — stated as the two TT$ totals beside it, not as
 * the US$ profit restated.
 *
 * The Pricing Summary shows three TT$ figures side by side: Total Cost, Total
 * Selling and Profit. Total Cost is the sum of the LINES' own TT$
 * (`sumCostTtd`, which reads each line's pinned `unitPriceTtd`), while Profit
 * used to be `ttdFromUsd(totals.profit)` — the US$ profit, whose cost side is
 * the STORED US$ cost, which is itself a ceiled restatement of that same
 * pinned TT$ (see `unitCostTtd` above: TT$100.00 at 6.5 stores US$15.39 and
 * reads back TT$100.04). Two different cost figures, so the three cards only
 * ever reconciled by luck: QUO-00072 read TT$260.46 where 1118.39 - 856.26 is
 * 262.13, QUO-00151 TT$322.73 where 1398.48 - 1075.00 is 323.48 — the gap is
 * the per-line ceiling drift, and it grows with the line count.
 *
 * So profit is COMPOSED from the two figures on screen instead of converted
 * separately. `netSellingUsd` is the AFTER-discount total, because profit is
 * after the discount (BUSINESS_RULES#quotation-totals-profit-margin-discount);
 * the US$ owner `resolveQuotationTotals` still decides what profit IS — this
 * only decides how it is SAID in TT$.
 *
 * Null when either side is, for the one reason either can be: no usable rate.
 */
export const quotationProfitTtd = (
  items: ReadonlyArray<PricedLine> | null | undefined,
  netSellingUsd: number | null | undefined,
  ttdToUsd: number | null | undefined
): number | null => {
  const figures = resolveProfitTtd(items, netSellingUsd, ttdToUsd);
  return figures ? figures.profit : null;
};

export interface ProfitPercents {
  profitPercentOfCost: number;
  marginPercent: number;
}

/**
 * Profit as a percentage of COST (`profitPercentOfCost`, the markup the line
 * realised) and of the FINAL SELLING PRICE (`marginPercent`) — the ONE spelling
 * of both, in whichever currency the three figures are stated. The grid's two
 * Item Profit columns, the Pricing Summary and the competitor box all read these
 * two numbers; each used to divide for itself. 0, never Infinity/NaN, when the
 * denominator is not positive (the long-standing contract of both fields).
 */
export function profitPercents(profit: number, cost: number, selling: number): ProfitPercents {
  return {
    profitPercentOfCost: cost > 0 ? (profit / cost) * 100 : 0,
    marginPercent: selling > 0 ? (profit / selling) * 100 : 0,
  };
}

export interface ProfitTtd extends ProfitPercents {
  costTtd: number;
  sellingTtd: number;
  profit: number;
}

/**
 * `quotationProfitTtd` with its working shown: the TT$ cost, TT$ selling,
 * TT$ profit and both percentages OF THOSE TT$ FIGURES. Works for one line
 * (`[item]` plus that line's `netSellingPrice`) or the whole quote.
 *
 * The TT$ percentages are computed from the TT$ figures, not copied from the
 * US$ ones: the TT$ cost is the pinned `unitPriceTtd`, not the ceiled US$ cost
 * restated, so the percentage printed beside TT$ money must be the one that
 * TT$ money actually gives. The two currencies agree closely on a normal line
 * (TT$100: 17.02% vs 17.07%) but NOT on a sub-cent US$ cost, which the stored
 * US$ ceils up hard: TT$0.05 x 1000 at 30% reads US$ 100.0% vs TT$ 160.0% on
 * cost. Each is exact for its own money.
 *
 * Per-line TT$ profits need not sum to the quote's: each line ceils its own
 * TT$ selling, the quote ceils once, so the lines can total up to a cent per
 * line more — the same "the box is the authority" rule the US$ lines follow.
 */
export function resolveProfitTtd(
  items: ReadonlyArray<PricedLine> | null | undefined,
  netSellingUsd: number | null | undefined,
  ttdToUsd: number | null | undefined
): ProfitTtd | null {
  const costTtd = sumCostTtd(items, ttdToUsd);
  if (costTtd == null) return null;
  const sellingTtd = ttdFromUsd(netSellingUsd, ttdToUsd);
  if (sellingTtd == null) return null;
  const profit = round2(sellingTtd - costTtd);
  return { costTtd, sellingTtd, profit, ...profitPercents(profit, costTtd, sellingTtd) };
}
